import LeadFilterTopbar, { WAITLIST_STATUS_OPTIONS } from 'bundles/leads/shared/lead_filter_topbar';
import React, { useState } from 'react';
import { RESULTS_PER_PAGE } from './list';
import SearchPage from 'components/search_page';
import PropTypes from 'prop-types';
import uniqBy from 'lodash/uniqBy';
import AllFiltersModal from '../../../leads/index/all_filters_modal';
import { CA_STATUS_OPTIONS } from '../../../leads/shared/lead_filter_topbar';

const ROLE_OPTIONS = [
  { id: 'broker', value: 'broker', label: 'Broker' },
  { id: 'owner', value: 'owner', label: 'Principal/Investor' },
];

export default function LeadFilters({
  currentLeads,
  currentPage,
  filters,
  leads,
  leadReferralSources,
  leadStatuses,
  listings,
  shownLeads,
  totalPages,
  onClearFilters,
  onFilterChange,
  onPageChange,
}) {
  const [showAllFilters, setShowAllFilters] = useState(false);

  function handleFilterChange(key, newValue) {
    onPageChange(1);
    onFilterChange({ [key]: newValue });
  }

  const companies = (function () {
    const companyValues = leads.map(lead => lead.contactCompany)
      .filter(option => !!option)
      .map(option => ({ value: option, label: option }));

    return uniqBy(companyValues, 'value');
  }());

  function transformArrayWithId(array) {
    return array.map(element => ({ ...element, value: element.id.toString() }));
  }

  function listingLabel(listing) {
    const prefix = listingPrefix(listing);
    if (listing.type === 'SaleListing') return prefix;

    return `${prefix} - ${listing.address2 || listing.number}`;
  }

  function listingPrefix(listing) {
    switch (listing.type) {
    case 'SaleListing':
      return 'Sale Listing';
    case 'LeaseSpaceListing':
      return 'Lease Space';
    case 'LotListing':
      return 'Lot';
    case 'CondoListing':
      return 'Condo';
    }
  }

  function formattedListings() {
    return listings.map(listing => ({ value: listing.id.toString(), label: listingLabel(listing) }));
  }

  const stringifiedFilters = function () {
    return Object.keys(filters).reduce((accumulator, filterKey) => {
      if (Array.isArray(filters[filterKey])) {
        accumulator[filterKey] = filters[filterKey].map(String);
      } else {
        accumulator[filterKey] = filters[filterKey];
      }

      return accumulator;
    }, {});
  }();

  function listingVariables() {
    if (listings.length === 1) return {};

    return {
      listings: formattedListings(),
      onChangeListingFilter: newValue => handleFilterChange('listingFilter', newValue.map(Number))
    };
  }

  const dataForFilters = {
    companies,
    leadReferralSources: transformArrayWithId(leadReferralSources),
    leadStatuses: transformArrayWithId(leadStatuses),
    profileUserTypes: ROLE_OPTIONS,
    caOptions: CA_STATUS_OPTIONS,
    waitlistOptions: WAITLIST_STATUS_OPTIONS,
    listings: formattedListings()
  };

  const dynamicFilters = [
    { dataKey: 'companies', filterKey: 'companyFilter', label: 'Companies' },
    { dataKey: 'profileUserTypes', filterKey: 'profileUserTypeFilter', label: 'Roles / Job Title' },
    { dataKey: 'leadStatuses', filterKey: 'statusFilter', label: 'Lead Status' },
    { dataKey: 'caOptions', filterKey: 'caStatusFilter', label: 'CA Status' },
    { dataKey: 'waitlistOptions', filterKey: 'waitlistFilter', label: 'Waitlist Status' },
    { dataKey: 'leadReferralSources', filterKey: 'referralSourceFilter', label: 'Sources' },
    { dataKey: 'listings', filterKey: 'listingFilter', label: 'Spaces' }
  ];

  function updateAllFilters(updatedFilters) {
    const formatToNumber = ['listingFilter', 'statusFilter', 'referralSourceFilter'];
    const formattedFilters = Object.keys(updatedFilters).reduce((accumulator, filterKey) => {
      const filterValues = updatedFilters[filterKey];
      if (formatToNumber.includes(filterKey)) {
        accumulator[filterKey] = filterValues.map(Number);
      } else {
        accumulator[filterKey] = filterValues;
      }

      return accumulator;
    }, {});

    onFilterChange(formattedFilters);
  }

  return (
    <SearchPage>
      <AllFiltersModal
        closeModal={() => setShowAllFilters(false)}
        dataForFilters={dataForFilters}
        dynamicFilters={dynamicFilters}
        show={showAllFilters}
        staringFilters={stringifiedFilters}
        onChangeFilters={updateAllFilters}
        onClearFilters={onClearFilters}
      />

      <div className="w-100">
        <LeadFilterTopbar
          companies={companies}
          currentPage={currentPage}
          leadCount={currentLeads.length}
          leadReferralSources={transformArrayWithId(leadReferralSources)}
          leadStatuses={transformArrayWithId(leadStatuses)}
          minimizeSpacing
          pageSize={RESULTS_PER_PAGE}
          profileUserTypes={ROLE_OPTIONS}
          toggleAllFiltersModal={() => setShowAllFilters(!showAllFilters)}
          totalCount={shownLeads.length}
          totalPages={totalPages}
          waitlistStatuses={WAITLIST_STATUS_OPTIONS}
          onChangeCaStatusFilter={newValue => handleFilterChange('caStatusFilter', newValue)}
          onChangeCompanyFilter={newValue => handleFilterChange('companyFilter', newValue)}
          onChangeEndDate={newValue => handleFilterChange('endDate', newValue)}
          onChangeProfileUserTypeFilter={newValue => handleFilterChange('profileUserTypeFilter', newValue)}
          onChangeReferralSourceFilter={newValue => handleFilterChange('referralSourceFilter', newValue.map(Number))}
          onChangeSearchText={newValue => handleFilterChange('searchText', newValue)}
          onChangeStartDate={newValue => handleFilterChange('startDate', newValue)}
          onChangeStatusFilter={newValue => handleFilterChange('statusFilter', newValue.map(Number))}
          onChangeWaitlistFilter={newValue => handleFilterChange('waitlistFilter', newValue)}
          onClearFilters={onClearFilters}
          {...stringifiedFilters}
          {...listingVariables()}
        />
      </div>
    </SearchPage>
  );
}

LeadFilters.propTypes = {
  currentLeads: PropTypes.array.isRequired,
  currentPage: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    caStatusFilter: PropTypes.array.isRequired,
    companyFilter: PropTypes.array.isRequired,
    searchText: PropTypes.string.isRequired,
    statusFilter: PropTypes.array.isRequired,
    listingFilter: PropTypes.array.isRequired,
    profileUserTypeFilter: PropTypes.array.isRequired,
    referralSourceFilter: PropTypes.array.isRequired,
    waitlistFilter: PropTypes.array.isRequired
  }).isRequired,
  leadReferralSources: PropTypes.array.isRequired,
  leads: PropTypes.array.isRequired,
  leadStatuses: PropTypes.array.isRequired,
  listings: PropTypes.array.isRequired,
  shownLeads: PropTypes.array.isRequired,
  totalPages: PropTypes.number.isRequired,
  onClearFilters: PropTypes.func.isRequired,
  onFilterChange: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
};
